"use client";

import { initializeApp, getApps, deleteApp, FirebaseApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, Auth } from "firebase/auth";
import type { FirebaseConfig } from "./firebaseConfig";

/**
 * Tenant-aware Firebase instance manager.
 * Unlike the Ballon d'Or single-tenant approach, we manage Firebase instances
 * per-tenant since each tenant can have different Firebase credentials.
 */
class TenantFirebaseManager {
    private currentConfig: FirebaseConfig | null = null;
    private app: FirebaseApp | null = null;
    private auth: Auth | null = null;
    private googleProvider: GoogleAuthProvider | null = null;

    /**
     * Check if Firebase is configured for the given config
     */
    isConfigured(): boolean {
        return this.app !== null && this.auth !== null;
    }

    /**
     * Initialize Firebase with tenant-specific configuration.
     * If Firebase was initialized with different config, it will be reinitialized.
     */
    initialize(config: FirebaseConfig): { app: FirebaseApp; auth: Auth; googleProvider: GoogleAuthProvider } {
        // Check if we need to reinitialize
        const configChanged =
            !this.currentConfig ||
            this.currentConfig.apiKey !== config.apiKey ||
            this.currentConfig.projectId !== config.projectId;

        if (configChanged) {
            // Clean up existing Firebase instance
            if (this.app) {
                deleteApp(this.app).catch(console.warn);
            }

            // Clear any existing apps (safety measure)
            getApps().forEach((app) => {
                deleteApp(app).catch(console.warn);
            });

            // Initialize new Firebase app
            this.app = initializeApp({
                apiKey: config.apiKey,
                authDomain: config.authDomain,
                projectId: config.projectId,
                appId: config.appId,
            });

            this.auth = getAuth(this.app);
            this.googleProvider = new GoogleAuthProvider();
            this.googleProvider.setCustomParameters({
                prompt: "select_account",
            });

            this.currentConfig = config;
        }

        return {
            app: this.app!,
            auth: this.auth!,
            googleProvider: this.googleProvider!,
        };
    }

    /**
     * Get current Firebase auth instance (if initialized)
     */
    getAuth(): Auth | null {
        return this.auth;
    }

    /**
     * Get current Google provider (if initialized)
     */
    getGoogleProvider(): GoogleAuthProvider | null {
        return this.googleProvider;
    }

    /**
     * Clean up Firebase instance
     */
    async cleanup(): Promise<void> {
        if (this.app) {
            await deleteApp(this.app).catch(console.warn);
            this.app = null;
            this.auth = null;
            this.googleProvider = null;
            this.currentConfig = null;
        }
    }
}

// Singleton instance for the client
export const firebaseManager = new TenantFirebaseManager();
